import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import RecruitmentCompanyBottomNavigation from '../../components/RecruitmentCompanyBottomNavigation';
import RecruitmentCompanyNav from '../../components/RecruitmentCompanyNav';
import Loader from '../../components/Loader';
import { useTranslation } from '../../hooks/useTranslation';
import notificationService from '../../services/notificationService';
import { toast } from 'react-toastify';

const RecruitmentCompanyNotifications = () => {
    const navigate = useNavigate();
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    useEffect(() => {
        loadNotifications();
    }, []);

    const loadNotifications = async (page = 1) => {
        try {
            setLoading(true);
            const response = await notificationService.getAll(page);
            if (response.success && response.data) {
                // Laravel pagination returns: { data: [...], current_page, last_page, next_page_url, etc. }
                const newNotifications = response.data.data || [];
                if (page === 1) {
                    setNotifications(newNotifications);
                } else {
                    setNotifications(prev => [...prev, ...newNotifications]);
                }
                // Check if there are more pages
                setHasMore(response.data.current_page < response.data.last_page);
                setCurrentPage(page);
            }
        } catch (error) {
            console.error('Error loading notifications:', error);
            toast.error(t('company.notifications.loadFailed') || 'Failed to load notifications');
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsRead = async (notificationId) => {
        try {
            await notificationService.markAsRead(notificationId);
            setNotifications(prev =>
                prev.map(notif =>
                    notif.id === notificationId
                        ? { ...notif, read_at: new Date().toISOString() }
                        : notif
                )
            );
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead();
            setNotifications(prev =>
                prev.map(notif => ({ ...notif, read_at: new Date().toISOString() }))
            );
            toast.success(t('company.notifications.allMarkedAsRead') || 'All notifications marked as read');
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const handleNotificationClick = async (notification) => {
        // Mark as read if unread
        if (!notification.read_at) {
            await handleMarkAsRead(notification.id);
        }

        // Navigate based on notification type
        const data = notification.data || {};
        if (data.type === 'application_received' && data.vacancy_id && data.application_id) {
            navigate(`/recruitment-company/vacancies/${data.vacancy_id}/applications`);
        }
    };

    const formatTimeAgo = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = now - date; // Time difference in milliseconds
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return t('employee.vacancyDetail.timeAgo.today');
        if (diffDays === 1) return t('employee.vacancyDetail.timeAgo.yesterday');
        if (diffDays < 7) return t('employee.vacancyDetail.timeAgo.daysAgo').replace('{days}', diffDays);
        if (diffDays < 30) return t('employee.vacancyDetail.timeAgo.weeksAgo').replace('{weeks}', Math.floor(diffDays / 7));
        if (diffDays < 365) return t('employee.vacancyDetail.timeAgo.monthsAgo').replace('{months}', Math.floor(diffDays / 30));
        return t('employee.vacancyDetail.timeAgo.yearsAgo').replace('{years}', Math.floor(diffDays / 365));
    };

    const getNotificationMessage = (notification) => {
        const data = notification.data || {};
        if (locale === 'ar') {
            return data.message_ar || data.message || '';
        }
        return data.message || data.message_ar || '';
    };

    const getNotificationTitle = (notification) => {
        const data = notification.data || {};
        if (locale === 'ar') {
            return data.title_ar || data.title || '';
        }
        return data.title || data.title_ar || '';
    };

    const unreadCount = notifications.filter(n => !n.read_at).length;

    return (
        <div className="employee-home-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            <RecruitmentCompanyNav user={user} />

            {/* Header */}
            <div className="px-3 mb-3 mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h4 className="mb-0">{t('company.notifications.title') || 'Notifications'}</h4>
                    {unreadCount > 0 && (
                        <button
                            onClick={handleMarkAllAsRead}
                            className="btn-view-vacancy"
                            style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.85rem'
                            }}
                        >
                            {t('company.notifications.markAllAsRead') || 'Mark all as read'}
                        </button>
                    )}
                </div>
            </div>

            {/* Notifications List */}
            <div className="px-3 mb-5">
                {loading && notifications.length === 0 ? (
                    <Loader size="medium" />
                ) : notifications.length === 0 ? (
                    <div className="text-center py-5">
                        <p className="empty-state-text">{t('company.notifications.noNotifications') || 'No notifications'}</p>
                    </div>
                ) : (
                    <div className="notifications-list">
                        {notifications.map((notification) => {
                            const isUnread = !notification.read_at;
                            const data = notification.data || {};

                            return (
                                <div
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification)}
                                    style={{
                                        background: isUnread ? '#f0f7ff' : 'white',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '12px',
                                        padding: '16px',
                                        marginBottom: '12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        borderLeft: isUnread ? '4px solid #4a7bc4' : '4px solid transparent'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = isUnread ? '#e8f2ff' : '#f9fafb';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = isUnread ? '#f0f7ff' : 'white';
                                    }}
                                >
                                    <div className="d-flex justify-content-between align-items-start gap-2">
                                        <div className="flex-grow-1">
                                            <h5
                                                style={{
                                                    margin: 0,
                                                    marginBottom: '8px',
                                                    fontSize: '16px',
                                                    fontWeight: isUnread ? '600' : '500',
                                                    color: '#111'
                                                }}
                                            >
                                                {getNotificationTitle(notification)}
                                            </h5>
                                            <p
                                                style={{
                                                    margin: 0,
                                                    marginBottom: '8px',
                                                    fontSize: '14px',
                                                    color: '#6b7280',
                                                    lineHeight: '1.5'
                                                }}
                                            >
                                                {getNotificationMessage(notification)}
                                            </p>
                                            <span
                                                style={{
                                                    fontSize: '12px',
                                                    color: '#9ca3af'
                                                }}
                                            >
                                                {formatTimeAgo(notification.created_at)}
                                            </span>
                                        </div>
                                        {isUnread && (
                                            <div
                                                style={{
                                                    width: '10px',
                                                    height: '10px',
                                                    borderRadius: '50%',
                                                    background: '#4a7bc4',
                                                    flexShrink: 0,
                                                    marginTop: '4px'
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {hasMore && !loading && (
                    <div className="text-center mt-3">
                        <button
                            onClick={() => loadNotifications(currentPage + 1)}
                            className="btn-view-vacancy"
                        >
                            {t('company.notifications.loadMore') || 'Load More'}
                        </button>
                    </div>
                )}
            </div>

            <RecruitmentCompanyBottomNavigation />
        </div>
    );
};

export default RecruitmentCompanyNotifications;

